import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface PlacementMetrics {
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
}

interface PlacementSummaryItem {
  placement: string;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  acos: number | null;
}

interface PerfSummary {
  uniqueKeywords: number;
  totalSpend: number;
  totalSales: number;
  totalImpressions: number;
  totalClicks: number;
  totalOrders: number;
  acos: number | null;
}

interface PerfKeyword {
  keyword: string;
  match_type: string;
  marketplace: string;
  marketplaceCode: string;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  orders: number;
  acos: number | null;
  cpc: number | null;
  cvr: number | null;
  placements: Record<string, PlacementMetrics> | null;
}

interface PerfData {
  summary: PerfSummary;
  placementSummary: PlacementSummaryItem[];
  keywords: PerfKeyword[];
}

interface ClassSummary {
  classification: string;
  termCount: number;
  totalSpend: number;
  totalSales: number;
}

interface SearchTerm {
  search_term: string;
  marketplace: string;
  marketplaceCode: string;
  parent_keyword: string | null;
  impressions: number;
  clicks: number;
  spend: number;
  sales: number;
  orders: number;
  acos: number | null;
  cpc: number | null;
  classification: string;
}

interface SearchData {
  classificationSummary: ClassSummary[];
  searchTerms: SearchTerm[];
}

interface TopAsin {
  asin: string;
  clickShare: number;
  conversionShare: number;
  isOurs: boolean;
}

interface TrendTerm {
  searchTerm: string;
  searchFrequencyRank: number;
  marketplace: string;
  date: string;
  department: string | null;
  topAsins: TopAsin[];
  hasOurProduct: boolean;
}

interface TrendsData {
  latestDate: string | null;
  ourAsins: string[];
  totalTerms: number;
  ourTermsCount: number;
  terms: TrendTerm[];
}

interface RankRow {
  marketplace: string;
  asin: string;
  keyword: string;
  date: string;
  organic_rank: number | null;
  ads_rank: number | null;
  prev_organic: number | null;
  prev_ads: number | null;
  title: string | null;
}

type SortKey = 'spend' | 'sales' | 'acos' | 'impressions';

type Tab = 'performance' | 'searchTerms' | 'trends' | 'ranks';

const TABS: { key: Tab; label: string }[] = [
  { key: 'performance', label: 'Keyword Performance' },
  { key: 'searchTerms', label: 'Search Terms' },
  { key: 'trends', label: 'Market Trends' },
  { key: 'ranks', label: 'Rank Tracking' },
];

// ── Main Component ───────────────────────────────────────────────────────────

export function KeywordRanking() {
  const [tab, setTab] = useState<Tab>('performance');
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);

  // Tab data
  const [perfData, setPerfData] = useState<PerfData | null>(null);
  const [searchData, setSearchData] = useState<SearchData | null>(null);
  const [trendsData, setTrendsData] = useState<TrendsData | null>(null);
  const [rankData, setRankData] = useState<RankRow[] | null>(null);

  // Load data when tab or days changes
  useEffect(() => {
    setLoading(true);
    if (tab === 'performance') {
      api.get<PerfData>(`/keywords/performance?days=${days}`)
        .then(setPerfData).catch(() => {}).finally(() => setLoading(false));
    } else if (tab === 'searchTerms') {
      api.get<SearchData>(`/keywords/search-terms?days=${days}`)
        .then(setSearchData).catch(() => {}).finally(() => setLoading(false));
    } else if (tab === 'trends') {
      api.get<TrendsData>('/keywords/market-trends')
        .then(setTrendsData).catch(() => {}).finally(() => setLoading(false));
    } else {
      api.get<RankRow[]>(`/keywords/rankings?days=${days}`)
        .then(r => setRankData(r.filter(row => row.organic_rank != null || row.ads_rank != null)))
        .catch(() => {}).finally(() => setLoading(false));
    }
  }, [tab, days]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Keyword Intelligence</h1>
        <p className="mt-1 text-sm text-gray-500">PPC keyword performance, search term discovery, and market trends</p>
      </div>

      {/* Tabs + Period */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1.5">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t.key ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>{t.label}</button>
          ))}
        </div>
        {tab !== 'trends' && (
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
          </select>
        )}
      </div>

      {tab === 'ranks' && <RankFreshnessBar />}

      {loading ? <Skeleton /> : (
        <>
          {tab === 'performance' && perfData && <PerformanceTab data={perfData} />}
          {tab === 'searchTerms' && searchData && <SearchTermsTab data={searchData} />}
          {tab === 'trends' && trendsData && <TrendsTab data={trendsData} />}
          {tab === 'ranks' && rankData && <RankTrackingTab data={rankData} />}
        </>
      )}
    </div>
  );
}

// ── Tab 1: Keyword Performance ───────────────────────────────────────────────

function PerformanceTab({ data }: { data: PerfData }) {
  const [sortBy, setSortBy] = useState<SortKey>('spend');
  const s = data.summary;

  const sorted = [...data.keywords].sort((a, b) => {
    if (sortBy === 'acos') return (a.acos ?? 999) - (b.acos ?? 999);
    return (b[sortBy] ?? 0) - (a[sortBy] ?? 0);
  });

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Card label="Total Spend" value={fmtDollar(s.totalSpend)} />
        <Card label="Total Sales" value={fmtDollar(s.totalSales)} color={s.totalSales > 0 ? 'green' : undefined} />
        <Card label="ACoS" value={s.acos != null ? fmtPct(s.acos) : '—'}
          color={s.acos != null ? (s.acos < 0.3 ? 'green' : s.acos > 0.5 ? 'red' : undefined) : undefined} />
        <Card label="Keywords" value={String(s.uniqueKeywords)}
          sub={`${s.totalClicks.toLocaleString()} clicks`} />
      </div>

      {/* Placement breakdown */}
      {data.placementSummary && data.placementSummary.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-3">
          {data.placementSummary.map(p => {
            const label = p.placement === 'TOP_OF_SEARCH' ? 'Top of Search'
              : p.placement === 'DETAIL_PAGE' ? 'Product Pages'
              : p.placement === 'REST_OF_SEARCH' ? 'Rest of Search' : p.placement;
            return (
              <div key={p.placement} className="rounded-xl border border-gray-200 bg-white p-3">
                <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{label}</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-lg font-bold text-gray-900">{fmtDollar(p.spend)}</span>
                  <span className="text-sm text-green-600">{fmtDollar(p.sales)}</span>
                  <AcosBadge value={p.acos} />
                </div>
                <p className="text-xs text-gray-400 mt-0.5">
                  {p.impressions.toLocaleString()} impr / {p.clicks.toLocaleString()} clicks
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <Th align="left">Keyword</Th>
                <Th align="center">Match</Th>
                <Th align="center">Mkt</Th>
                <Th align="right" sort="impressions" current={sortBy} onSort={setSortBy}>Impr</Th>
                <Th align="right" sort="spend" current={sortBy} onSort={setSortBy}>Spend</Th>
                <Th align="right" sort="sales" current={sortBy} onSort={setSortBy}>Sales</Th>
                <Th align="right">Orders</Th>
                <Th align="right" sort="acos" current={sortBy} onSort={setSortBy}>ACoS</Th>
                <Th align="right">CPC</Th>
                <Th align="right">CVR</Th>
                <Th align="center">ToS %</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {sorted.map((k, i) => {
                const tos = k.placements?.['TOP_OF_SEARCH'];
                const totalImpr = k.placements
                  ? Object.values(k.placements).reduce((s, p) => s + (p.impressions ?? 0), 0)
                  : 0;
                const tosPct = tos && totalImpr > 0 ? tos.impressions / totalImpr : null;
                return (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 font-medium max-w-[200px] truncate">{k.keyword}</td>
                    <td className="px-3 py-2 text-center">
                      <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                        {k.match_type?.slice(0, 5) ?? '—'}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center text-xs text-gray-500">{k.marketplaceCode}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">{k.impressions.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtDollar(k.spend)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-green-700">{fmtDollar(k.sales)}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">{k.orders}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      <AcosBadge value={k.acos} />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">{k.cpc != null ? fmtDollar(k.cpc) : '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">{k.cvr != null ? fmtPct(k.cvr) : '—'}</td>
                    <td className="px-3 py-2 text-center tabular-nums">
                      {tosPct != null ? (
                        <span className={`text-xs font-medium ${tosPct > 0.5 ? 'text-green-600' : tosPct > 0.2 ? 'text-amber-600' : 'text-gray-500'}`}>
                          {(tosPct * 100).toFixed(0)}%
                        </span>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {sorted.length === 0 && <p className="p-6 text-center text-sm text-gray-400">No keyword performance data yet.</p>}
      </div>
    </div>
  );
}

// ── Tab 2: Search Terms ──────────────────────────────────────────────────────

const CLS_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  WINNER: { bg: 'bg-green-50', text: 'text-green-700', dot: 'bg-green-500' },
  WASTER: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500' },
  PROMISING: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
  LOW_DATA: { bg: 'bg-gray-50', text: 'text-gray-500', dot: 'bg-gray-400' },
};

function SearchTermsTab({ data }: { data: SearchData }) {
  const [filter, setFilter] = useState<string | null>(null);

  const filtered = filter
    ? data.searchTerms.filter(t => t.classification === filter)
    : data.searchTerms;

  return (
    <div className="space-y-4">
      {/* Classification cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {['WINNER', 'PROMISING', 'WASTER', 'LOW_DATA'].map(cls => {
          const s = data.classificationSummary.find(c => c.classification === cls);
          const c = CLS_COLORS[cls]!;
          return (
            <button key={cls} onClick={() => setFilter(filter === cls ? null : cls)}
              className={`rounded-xl border p-3 text-left transition-all ${
                filter === cls ? `border-2 ${c.bg} border-current ${c.text}` : 'border-gray-200 bg-white hover:bg-gray-50'
              }`}>
              <div className="flex items-center gap-2">
                <div className={`h-2 w-2 rounded-full ${c.dot}`} />
                <span className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{cls.replace('_', ' ')}</span>
              </div>
              <p className="mt-1 text-xl font-bold text-gray-900">{s?.termCount ?? 0}</p>
              <p className="text-xs text-gray-400">
                {fmtDollar(s?.totalSpend ?? 0)} spend / {fmtDollar(s?.totalSales ?? 0)} sales
              </p>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <Th align="left">Search Term</Th>
                <Th align="center">Mkt</Th>
                <Th align="right">Impr</Th>
                <Th align="right">Clicks</Th>
                <Th align="right">Spend</Th>
                <Th align="right">Sales</Th>
                <Th align="right">ACoS</Th>
                <Th align="center">Class</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((t, i) => {
                const c = CLS_COLORS[t.classification] ?? CLS_COLORS.LOW_DATA!;
                return (
                  <tr key={i} className={`${c.bg} hover:brightness-95`}>
                    <td className="px-3 py-2 text-gray-900 font-medium max-w-[250px] truncate">{t.search_term}</td>
                    <td className="px-3 py-2 text-center text-xs text-gray-500">{t.marketplaceCode}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">{t.impressions.toLocaleString()}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-gray-600">{t.clicks}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtDollar(t.spend)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-green-700">{fmtDollar(t.sales)}</td>
                    <td className="px-3 py-2 text-right tabular-nums"><AcosBadge value={t.acos} /></td>
                    <td className="px-3 py-2 text-center">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${c.text}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                        {t.classification.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <p className="p-6 text-center text-sm text-gray-400">No search terms found.</p>}
      </div>
    </div>
  );
}

// ── Tab 3: Market Trends ─────────────────────────────────────────────────────

function TrendsTab({ data }: { data: TrendsData }) {
  const [showOursOnly, setShowOursOnly] = useState(false);
  const [search, setSearch] = useState('');

  let terms = data.terms;
  if (showOursOnly) terms = terms.filter(t => t.hasOurProduct);
  if (search) terms = terms.filter(t => t.searchTerm.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Card label="Top Search Terms" value={String(data.totalTerms)} />
        <Card label="Your Products Appear In" value={String(data.ourTermsCount)} color="green"
          sub={`of ${data.totalTerms} terms`} />
        <Card label="Latest Data" value={data.latestDate ?? 'No data'} />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => setShowOursOnly(!showOursOnly)}
          className={`rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors ${
            showOursOnly ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}>
          {showOursOnly ? 'Our Products Only' : 'Show All'}
        </button>
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Filter by search term..."
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm w-64" />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <Th align="right">#</Th>
                <Th align="left">Search Term</Th>
                <Th align="center">Mkt</Th>
                <Th align="left">Top 3 ASINs (click share / conversion share)</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {terms.map((t, i) => (
                <tr key={i} className={`hover:bg-gray-50 ${t.hasOurProduct ? 'bg-green-50/50' : ''}`}>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-400 text-xs w-12">
                    {t.searchFrequencyRank.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-gray-900 font-medium max-w-[250px] truncate">{t.searchTerm}</td>
                  <td className="px-3 py-2 text-center text-xs text-gray-500">{t.marketplace}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-3 flex-wrap">
                      {t.topAsins.map((a, j) => (
                        <a key={j} href={`https://www.amazon.com/dp/${a.asin}`}
                          target="_blank" rel="noopener noreferrer"
                          className={`text-xs rounded px-1.5 py-0.5 hover:underline ${
                            a.isOurs ? 'bg-brand-100 text-brand-700 font-semibold' : 'text-brand-600 hover:text-brand-800'
                          }`}>
                          {a.asin}
                          <span className="text-[10px] ml-1 text-gray-400">
                            {((a.clickShare ?? 0) * 100).toFixed(1)}% / {((a.conversionShare ?? 0) * 100).toFixed(1)}%
                          </span>
                        </a>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {terms.length === 0 && (
          <p className="p-6 text-center text-sm text-gray-400">
            {showOursOnly ? 'None of your products appear in top search terms yet.' : 'No Brand Analytics data available. Weekly sync runs on Sundays.'}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Rank freshness + manual resync ───────────────────────────────────────────

interface RankFreshness {
  latest: string | null;
  ageDays: number | null;
  rankedKeywords: number;
  trackedKeywords: number;
}

function RankFreshnessBar() {
  const [info, setInfo] = useState<RankFreshness | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<{ text: string; tone: 'ok' | 'err' } | null>(null);

  const load = () => api.get<RankFreshness>('/keywords/rankings/freshness').then(setInfo).catch(() => {});
  useEffect(() => { load(); }, []);

  async function resync() {
    setSyncing(true);
    setMsg(null);
    try {
      const r = await api.post<{ dispatched: number; errorCount: number; blocked: string | null; sampleError: string | null }>('/keywords/resync');
      if (r.blocked) {
        setMsg({ text: r.blocked, tone: 'err' });
      } else if (r.dispatched > 0) {
        setMsg({ text: `Dispatched ${r.dispatched} keyword scrape(s). Rankings refresh within ~10 min — reload shortly.`, tone: 'ok' });
      } else if (r.errorCount > 0) {
        setMsg({ text: `No keywords dispatched. ${r.sampleError ?? ''}`, tone: 'err' });
      } else {
        setMsg({ text: 'Nothing to resync — all tracked keywords were already checked today.', tone: 'ok' });
      }
      load();
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : 'Resync failed', tone: 'err' });
    } finally {
      setSyncing(false);
    }
  }

  const stale = info?.ageDays != null && info.ageDays > 7;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white p-3">
      <div className="text-sm">
        {info ? (
          <>
            <span className={stale ? 'font-medium text-red-600' : 'text-gray-700'}>
              {info.latest
                ? `Rankings last updated ${info.latest}${info.ageDays != null ? ` (${info.ageDays}d ago)` : ''}`
                : 'No rankings collected yet'}
            </span>
            <span className="text-gray-400"> · {info.rankedKeywords}/{info.trackedKeywords} tracked keywords ranked</span>
          </>
        ) : (
          <span className="text-gray-400">Loading freshness…</span>
        )}
      </div>
      <button
        onClick={resync}
        disabled={syncing}
        className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
      >
        {syncing ? 'Resyncing…' : 'Resync now'}
      </button>
      {msg && (
        <p className={`w-full text-xs ${msg.tone === 'err' ? 'text-red-600' : 'text-green-600'}`}>{msg.text}</p>
      )}
    </div>
  );
}

// ── Tab 4: Rank Tracking with Heatmap ────────────────────────────────────────

interface HeatmapKeyword {
  keyword: string; asin: string; marketplace: string;
  days: Record<string, { organic: number | null; ads: number | null }>;
}

function rankColor(rank: number | null): string {
  if (rank == null) return 'bg-gray-100';
  if (rank <= 5) return 'bg-green-500 text-white';
  if (rank <= 10) return 'bg-green-300';
  if (rank <= 15) return 'bg-yellow-300';
  if (rank <= 25) return 'bg-orange-300';
  return 'bg-red-300';
}

function RankTrackingTab({ data }: { data: RankRow[] }) {
  const [heatmap, setHeatmap] = useState<{ dates: string[]; keywords: HeatmapKeyword[] } | null>(null);
  const [heatDays, setHeatDays] = useState(30);

  useEffect(() => {
    api.get<{ dates: string[]; keywords: HeatmapKeyword[] }>(`/rankings/heatmap?days=${heatDays}`)
      .then(setHeatmap)
      .catch(() => {});
  }, [heatDays]);

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <p className="text-sm text-gray-500">
          Rankings checked daily via Amazon search scraping (top ~66 results).
          {data.length === 0 && ' No rankings found yet.'}
        </p>
      </div>

      {/* Heatmap */}
      {heatmap && heatmap.keywords.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="p-3 border-b flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold">Rank Heatmap</h3>
              <div className="flex items-center gap-2 mt-1 text-[10px]">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-500" /> 1-5</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-green-300" /> 6-10</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-yellow-300" /> 11-15</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-orange-300" /> 16-25</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-300" /> 25+</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-gray-100" /> N/A</span>
              </div>
            </div>
            <div className="flex gap-1">
              {[14, 30, 60].map((d) => (
                <button key={d} onClick={() => setHeatDays(d)}
                  className={`px-2 py-0.5 text-xs rounded ${heatDays === d ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="text-[10px]">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 sticky left-0 bg-white z-10 min-w-[180px]">Keyword</th>
                  <th className="text-left px-1 py-1 min-w-[50px]">Mkt</th>
                  {heatmap.dates.map((d) => (
                    <th key={d} className="px-0 py-1 min-w-[22px] text-center text-gray-400 whitespace-nowrap">
                      {d.slice(8)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmap.keywords.map((kw, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="px-2 py-0.5 sticky left-0 bg-white z-10 truncate max-w-[180px] font-medium" title={kw.keyword}>
                      {kw.keyword}
                    </td>
                    <td className="px-1 py-0.5 text-gray-500">{kw.marketplace}</td>
                    {heatmap.dates.map((d) => {
                      const rank = kw.days[d]?.organic ?? null;
                      return (
                        <td key={d} className={`px-0 py-0.5 text-center ${rankColor(rank)}`}
                          title={rank != null ? `#${rank}` : 'Not found'}>
                          {rank != null ? rank : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Existing table */}
      {data.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <Th align="center">Mkt</Th>
                  <Th align="left">Keyword</Th>
                  <Th align="left">ASIN</Th>
                  <Th align="right">Organic</Th>
                  <Th align="right">Sponsored</Th>
                  <Th align="right">Change</Th>
                  <Th align="right">Date</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data.map((r, i) => {
                  const orgChange = r.prev_organic != null && r.organic_rank != null
                    ? r.prev_organic - r.organic_rank : null;
                  return (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-center text-xs text-gray-500">{r.marketplace}</td>
                      <td className="px-3 py-2 text-gray-900 font-medium max-w-[200px] truncate">{r.keyword}</td>
                      <td className="px-3 py-2">
                        <div className="min-w-0">
                          <span className="text-xs font-medium text-brand-600">{r.asin}</span>
                          {r.title && <p className="text-[10px] text-gray-400 truncate max-w-[180px]">{r.title}</p>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {r.organic_rank != null ? `#${r.organic_rank}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-blue-600">
                        {r.ads_rank != null ? `#${r.ads_rank}` : '—'}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {orgChange != null ? (
                          <span className={orgChange > 0 ? 'text-green-600' : orgChange < 0 ? 'text-red-600' : 'text-gray-400'}>
                            {orgChange > 0 ? `+${orgChange}` : orgChange === 0 ? '=' : String(orgChange)}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-3 py-2 text-right text-xs text-gray-400">{r.date}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared Components ────────────────────────────────────────────────────────

function Card({ label, value, color, sub }: { label: string; value: string; color?: 'green' | 'red'; sub?: string }) {
  const textColor = color === 'green' ? 'text-green-600' : color === 'red' ? 'text-red-600' : 'text-gray-900';
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-xl font-bold ${textColor}`}>
        {value}
        {sub && <span className="text-xs font-normal text-gray-400 ml-1">{sub}</span>}
      </p>
    </div>
  );
}

function AcosBadge({ value }: { value: number | null }) {
  if (value == null) return <span className="text-gray-400">—</span>;
  const color = value < 0.25 ? 'text-green-600' : value < 0.4 ? 'text-amber-600' : 'text-red-600';
  return <span className={`font-medium ${color}`}>{fmtPct(value)}</span>;
}

function Th({ children, align, sort, current, onSort }: {
  children: React.ReactNode;
  align: 'left' | 'center' | 'right';
  sort?: SortKey;
  current?: SortKey;
  onSort?: (s: SortKey) => void;
}) {
  const textAlign = align === 'left' ? 'text-left' : align === 'right' ? 'text-right' : 'text-center';
  const base = `px-3 pb-2 pt-3 ${textAlign} text-xs font-medium text-gray-500`;
  if (sort && onSort) {
    return (
      <th className={`${base} cursor-pointer hover:text-gray-700`} onClick={() => onSort(sort)}>
        {children} {current === sort ? '↓' : ''}
      </th>
    );
  }
  return <th className={base}>{children}</th>;
}

function Skeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-20 rounded-xl bg-gray-200" />)}
      </div>
      <div className="h-64 rounded-xl bg-gray-200" />
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtDollar(v: number): string {
  return `$${v.toFixed(2)}`;
}

function fmtPct(v: number): string {
  return `${(v * 100).toFixed(1)}%`;
}
